import ReactMarkdown from "react-markdown"
import tw from "twin.macro"
import Image from "next/image"
import Link from "next/link"
import { Attachment, FieldSet, Records } from "airtable"
import { ResourceType } from "../resources"

interface RecommendedImageProps {
  image_url: string
  caption: string
  link: string
}
export const RecommendedImage = ({ image_url, caption, link }: RecommendedImageProps) => {
  const altText = caption
    ? `${caption.toString()} – The Richard Pousette-Dart Foundation`
    : `The Richard Pousette-Dart Foundation`

  return (
    <Link href={link} passHref target="_new" tw="block h-52 overflow-hidden">
      <div tw="flex flex-col items-center justify-center h-full relative">
        <Image src={image_url} fill={true} alt={altText} tw="object-contain" />
      </div>
    </Link>
  )
}
interface MediaKickerProps {
  media: string
  link: string
}

const icon = (
  <svg width="14" height="14" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.0359 51.2737H37.1441C42.3902 51.2737 46.68 46.9839 46.68 41.7378V25.1717C46.68 24.0672 45.7786 23.1659 44.6741 23.1659C43.5696 23.1659 42.6683 24.0673 42.6683 25.1717V41.7378C42.6683 44.775 40.1813 47.262 37.1441 47.262H10.0359C6.99871 47.262 4.51175 44.775 4.51175 41.7378V14.6297C4.51175 11.5925 6.99871 9.1055 10.0359 9.1055H26.602C27.7065 9.1055 28.6079 8.20405 28.6079 7.09962C28.6079 5.99519 27.7065 5.09375 26.602 5.09375H10.0359C4.78981 5.09375 0.5 9.38356 0.5 14.6297V41.7378C0.499446 46.984 4.78986 51.2737 10.0359 51.2737Z"
      fill="#303333"
      stroke="#303333"
    />
    <path
      d="M35.7133 4.51175H44.6662L23.4638 25.5694L23.4638 25.5694L23.4626 25.5706C22.6648 26.3684 22.6648 27.5883 23.4625 28.3861C24.2603 29.184 25.4802 29.1838 26.278 28.3863C26.278 28.3862 26.278 28.3862 26.2781 28.3862L47.4876 7.24906V16.2107C47.4876 17.3152 48.389 18.2166 49.4934 18.2166C50.5979 18.2166 51.4992 17.3152 51.4992 16.2108V2.4308V2.12983V2.05472C51.4992 2.05189 51.4992 2.04884 51.4992 2.04558C51.4996 1.95646 51.5005 1.70881 51.2913 1.48933C51.268 1.43616 51.2429 1.39637 51.2275 1.37323C51.214 1.35278 51.2006 1.33469 51.1886 1.31928C51.0597 1.09484 50.8566 0.911138 50.632 0.757037C50.4677 0.615479 50.2925 0.584183 50.1824 0.57741L50.0278 0.525768L49.9507 0.5H49.8694H49.7943H49.4933H35.7133C34.6088 0.5 33.7074 1.40145 33.7074 2.50588C33.7074 3.61036 34.6089 4.51175 35.7133 4.51175Z"
      fill="#303333"
      stroke="#303333"
    />
  </svg>
)

export const MediaKicker = ({ media, link }: MediaKickerProps) => {
  if (!link) {
    return <></>
  }
  return (
    <p tw="pt-2 text-xs font-sans text-rpd-gray-8">
      <Link href={link} passHref tw="flex items-center space-x-2">
        <span>{media}</span> {icon}
      </Link>
    </p>
  )
}

export interface CardRecommendedProps {
  id: string
  details: string
  body: string
  image_url: string
  image_preview: Array<Attachment>
  caption: string
  link: string
  type: ResourceType
  media: string
}

const CardRecommended = (props: CardRecommendedProps) => {
  const { body, image_url, caption, type, link, media } = props

  if (link && type && body && media && image_url) {
    const captionText = caption ? caption.toString() : ""
    const pageType = type.toString()
    const bodyText = body.toString()
    const mediaText = media.toString()

    return (
      <div tw="col-span-2 tablet:(col-span-4) desktop:(col-span-3)">
        <div tw="space-y-4 tablet:(space-y-8)">
          <RecommendedImage image_url={image_url.toString()} caption={captionText} link={link.toString()} />
          <div>
            <h3 tw="uppercase font-sans text-sm text-rpd-blue font-bold">
              <Link href={link.toString()} target="_new" passHref>
                {pageType}
              </Link>
            </h3>
            <div tw="font-sans text-xs tablet-lg:(text-sm)">
              <ReactMarkdown>{bodyText}</ReactMarkdown>
            </div>
            <MediaKicker media={mediaText} link={link.toString()} />
          </div>
        </div>
      </div>
    )
  }
  return <></>
}

interface RecommendedProps {
  contentData: Records<FieldSet>
}

const Recommended = (props: RecommendedProps) => {
  const { contentData } = props

  return (
    <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
      <div tw="grid grid-cols-4 gap-4 gap-y-8 tablet:(grid-cols-12) desktop:(gap-6 gap-y-12)">
        {contentData.map((item: any, i: number) => {
          return <CardRecommended {...item.fields} key={i} />
        })}
      </div>
    </div>
  )
}

export default Recommended
